import React, {
  memo, useEffect, useRef, useState, useContext,
} from 'react';
import {
  Button,
  Col,
  DatePicker,
  Dropdown,
  Menu,
  Row,
  Select,
  Space,
  Spin,
} from 'antd';
import { DownOutlined } from '@ant-design/icons';
import classnames from 'classnames';
import moment from 'moment-timezone';
import {
  useParams, useHistory, Link, useLocation,
} from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import ReactGA from 'react-ga4';
import { useAuthenticator } from '@aws-amplify/ui-react';

import CustomChart from '../../../components/custom-chart';
import {
  periods,
} from '../constant';
import FeaturedParameters from '../components/FeaturedParameters';
import CustomSwitch from '../../../components/custom-switch';
import {
  formatParameterObservation,
  getParameters,
  getLatestParameterDate,
} from '../../../utils';
import PlatformEvent from '../../../components/parameter/PlatformEvent';
import DetailHeader from '../components/DetailHeader';
import appConfig, { brandingConfig } from '../../../config/index';
import NoDataInRange from './NoDataInRange';
import './ParameterDetail.scss';
import WindroseChart from '../../../components/custom-chart/windroseChart';
import { OptionsIcon } from '../../../components/icons';

import UserContext from '../../../contexts/UserContext';
import ParameterFilters from '../../../components/parameter/ParameterFilters';
import DatasetMessage from '../../../components/dataset-message';
import { useData } from '../../../contexts/DataContext';
import CtaBar from '../../../components/cta-bar';
import { CTA_BUTTONS } from '../../../components/cta-bar/constants';
import CustomModal from '../../../components/modal';
import SocialShareSenderModal from '../components/SocialShareSenderModal';
import { ParameterDetailsEvent } from '../../../constant/google-analytics/constant';
import CommunitySignupModal from '../components/CommunitySignupModal';
import DownloadChartModal from './DownloadChartModal';
import SocialShareRecipientModal from '../../dashboard/components/SocialShareRecipientModal';
import {
  getChartImage,
  postSharingImage,
  base64UrlSafeEncode,
  downloadChartImage,
} from '../../../utils/socialSharing';
import { featureFlag } from '../../../constant';
import CollectionStatusAlert from './CollectionStatusAlert';
import ParameterMessage from '../../../components/parameter-message';

const { Option } = Select;
const { RangePicker } = DatePicker;

const colorPalette = brandingConfig.map.parameters;

const ParameterDetail = () => {
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const history = useHistory();
  const { unitPreferences } = useContext(UserContext);
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);
  const {
    useObservationData,
    useGlobalData,
    utils: dataUtils,
  } = useData();
  const {
    id,
    parameterId,
    platformId,
  } = useParams();

  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);
  const collectionStatus = searchParams.get('collectionStatus') || null;

  const obsDatasetId = Number(id);

  const [chartData, setChartData] = useState([]);
  const [customFilterValue, setCustomFilterValue] = useState([moment().subtract(30, 'days'), moment()]);
  const [dataView, setDataView] = useState(true);
  const [parameter, setParameter] = useState();
  const [parameters, setParameters] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(periods[0]);
  const [parameterDepths, setParameterDepths] = useState([]);
  const [parameterIds, setParameterIds] = useState(parameterId);
  const [latestDate, setLatestDate] = useState(moment().format('YYYY-MM-DD HH:mm:ss'));
  const [dotsVisible, setDotsVisible] = useState(false);
  const [linesVisible, setLinesVisible] = useState(true);
  const [filterEnabled, setFilterEnabled] = useState(true);
  const [comparisonParameters, setComparisonParameters] = useState([]);
  const [categorySelected, setCategorySelected] = useState('');
  const [categoryArray, setCategoryArray] = useState([]);
  const obsDatasetIds = [Number(id)];
  const [filterValue, setFilterValue] = useState({
    parameterIds: [parameterId],
    obsDatasetId: obsDatasetIds,
    collectionStatus,
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareImageUrl, setShareImageUrl] = useState('');
  const [shareImageLink, setShareImageLink] = useState('');
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [showDownloadChartModal, setShowDownloadChartModal] = useState(false);
  const [isChartTooltipShowing, setIsChartTooltipShowing] = useState();

  const canvasRef = useRef(null);

  const isWindroseChart = (parameter?.standard_name === 'wind_from_direction');
  const windroseSvgNode = document.getElementById('seagull-windrose-chart-high-res');

  const optionsMenu = (
    <Menu>
      <Menu.Item>
        <Link
          to={{
            pathname: '/workspace-beta',
            search: `?datasetIds=${obsDatasetIds}&parameterIds=${parameterId}`,
          }}
        >
          See this data on workspace
        </Link>
      </Menu.Item>
      <Menu.Item onClick={() => setDotsVisible((prev) => !prev)}>
        {dotsVisible ? 'Turn off data points' : 'Turn on data points'}
      </Menu.Item>
      <Menu.Item onClick={() => setLinesVisible((prev) => !prev)}>
        {linesVisible ? 'Turn off lines' : 'Turn on lines'}
      </Menu.Item>
      {featureFlag.socialSharingTimeSeries && (
        <Menu.Item className="downloadChartSubMenu" onClick={() => setShowDownloadChartModal(true)}>
          Download Hi-Res Image
        </Menu.Item>
      )}
    </Menu>
  );

  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const [chartHeight, setChartHeight] = useState(0);
  const [isAllDepthsSelected, setIsAllDepthsSelected] = useState(false);
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const {
    obsDatasetSummariesResult,
    observationalV2DataResult,
    observationalV2DataIsLoading,
    observationalV2DataIsFetching,
  } = useObservationData(
    platformId,
    filterValue,
    parameterIds,
    obsDatasetIds,
    false,
  );

  const {
    parameterConfigurationsResult,
    organizationsResult,
    registeredParametersResult,
    observationalPlatformsResult,
    observationalLatestDataResult,
    observationalLatestDataIsLoading,
  } = useGlobalData(collectionStatus);

  useEffect(() => {
    if (observationalLatestDataResult?.length > 1) {
      const datasetObsLatest = observationalLatestDataResult.find((d) => d.obs_dataset_id === Number(obsDatasetIds[0]));
      const parameterObsLatest = datasetObsLatest?.parameters.find((p) => p.parameter_id === Number(parameterId));
      const latestTimestamp = parameterObsLatest?.observations[0]?.timestamp;
      setLatestDate(moment.unix(latestTimestamp).format('YYYY-MM-DD HH:mm:ss'));
    }
  }, [observationalLatestDataResult, parameterId]);

  // Filter API data depending on the duration selected
  useEffect(() => {
    switch (selectedPeriod) {
      case '24 hours':
        setFilterValue({
          ...filterValue,
          startDate: moment(latestDate).subtract(1, 'days').format('YYYY-MM-DD'),
          endDate: moment(latestDate).add(1, 'days').format('YYYY-MM-DD'),
        });
        break;
      case '7 days':
        setFilterValue({
          ...filterValue,
          startDate: moment(latestDate).subtract(7, 'days').format('YYYY-MM-DD'),
          endDate: moment(latestDate).add(1, 'days').format('YYYY-MM-DD'),
        });
        break;
      case '30 days':
        setFilterValue({
          ...filterValue,
          startDate: moment(latestDate).subtract(30, 'days').format('YYYY-MM-DD'),
          endDate: moment(latestDate).add(1, 'days').format('YYYY-MM-DD'),
        });
        break;
      default:
        setFilterValue({
          ...filterValue,
          startDate: customFilterValue[0].format('YYYY-MM-DD'),
          endDate: customFilterValue[1].format('YYYY-MM-DD'),
        });
    }
    setContainerHeight(containerRef.current.offsetHeight);
    setContainerWidth(containerRef.current.offsetHeight);
  }, [latestDate, selectedPeriod, customFilterValue]);

  const getObsDataset = () => {
    if (!obsDatasetSummariesResult || obsDatasetSummariesResult.length < 1 || !obsDatasetId) {
      return null;
    }

    return obsDatasetSummariesResult.find((o) => o.obs_dataset_id === obsDatasetId);
  };

  const obsDataset = getObsDataset();

  const datasetMessage = obsDataset?.obs_dataset_message?.message;

  const onDownloadPng = async () => {
    await downloadChartImage('png', 'seagull-share-chart-high-res', windroseSvgNode, obsDataset.org_platform_id, obsDataset.platform_name, parameter.name, parameter.unit);
  };

  const onDownloadSvg = async () => {
    await downloadChartImage('svg', 'seagull-share-chart-high-res', windroseSvgNode, obsDataset.org_platform_id, obsDataset.platform_name, parameter.name, parameter.unit);
  };

  // If none of chart data name values are equal to depth name values, depth data is present, and depth data is not empty set
  // then pull in what is already in chart data and  set name/data keys
  const formatChartData = (depth) => {
    if (depth) {
      if (!chartData.some((o) => o.name === depth.name) && depth.data && depth.data.length > 0) {
        setChartData([...chartData, { name: depth.name, data: depth.data }]);
      }
    }
  };

  const handleSetCategoryArray = (childData) => {
    setCategoryArray(childData);
  };

  const getOrganization = () => {
    if (!organizationsResult
      || organizationsResult.length < 1
      || !obsDatasetSummariesResult
      || obsDatasetSummariesResult.length < 1
    ) {
      return null;
    }

    const obsDataset = obsDatasetSummariesResult.find((o) => o.obs_dataset_id === obsDatasetId);

    if (!obsDataset) {
      return null;
    }

    const org = organizationsResult.find((o) => o.organization_id === obsDataset.organization_id);

    return org;
  };

  const organization = getOrganization();

  useEffect(() => {
    // TODO: Turn into a guard clause and return early.
    if (obsDatasetSummariesResult
      && obsDatasetSummariesResult.length > 0 //  Sets the dataset
      && observationalPlatformsResult
      && observationalPlatformsResult.length > 0
      && observationalLatestDataResult
      && parameterConfigurationsResult
      && parameterConfigurationsResult.length > 0
      && registeredParametersResult
    ) {
      const datasetFiltered = obsDatasetSummariesResult.find((o) => o.obs_dataset_id === Number(obsDatasetIds[0]));
      const foundDataset = observationalPlatformsResult.find((o) => o.properties.obs_dataset_id === Number(obsDatasetIds[0]));
      const latestDate = getLatestParameterDate(foundDataset.properties, observationalLatestDataResult, true);
      const timeSince = moment().diff(moment(latestDate), 'hours');
      const newDataset = datasetFiltered;
      if (timeSince > appConfig.oldDataCutoffInHours || Number.isNaN(timeSince)) {
        newDataset.obs_dataset_platform_assignment.platform.platform_event.event = 'unavailable';
        newDataset.obs_dataset_platform_assignment.platform.platform_event.last_updated_parameter_in_hours = timeSince;
      }

      const parametersData = getParameters({
        dataset: datasetFiltered,
        obsData: observationalLatestDataResult,
        parameterConfigurations: parameterConfigurationsResult,
        registeredParameters: registeredParametersResult,
        unitPreferences,
      });

      // Removing undefined parameters
      const filteredParametersData = parametersData.filter((param) => param.name !== undefined);
      setParameters(filteredParametersData);

      const selectedParameter = filteredParametersData.find((o) => o.parameter_id === Number(parameterId));
      setParameter(selectedParameter);
      if (comparisonParameters.length === 0) {
        setComparisonParameters([selectedParameter]);
      }

      // const organizationObj = organizationsResult?.find((o) => o.organization_id === datasetFiltered.organization_id);

      // setOrganization(organizationObj);

      let parameterIdsStr;

      if (selectedParameter?.derived_name === 'current_direction' || selectedParameter?.derived_name === 'current_speed') { //  if parameter selected is current speed or diretion then set parameter ID for url
        parameterIdsStr = filteredParametersData.filter(() => (
          selectedParameter?.eastward_sea_water_velocity_id !== undefined || selectedParameter?.northward_sea_water_velocity_id !== undefined
        )).map(((o) => o.parameter_id)).join(',');
      } else if (selectedParameter?.standard_name === 'wind_from_direction') {
        parameterIdsStr = filteredParametersData.filter((o) => (
          o.standard_name === 'wind_speed' || o.standard_name === 'wind_from_direction'
        )).map(((o) => o.parameter_id)).join(',');
      } else {
        parameterIdsStr = filteredParametersData.filter((o) => (
          o.name_vocabulary === selectedParameter?.name_vocabulary && o.standard_name === selectedParameter?.standard_name
        )).map(((o) => o.parameter_id)).join(',');
      }
      if (parameterIdsStr !== parameterIds) {
        setParameterIds(parameterIdsStr);
        setFilterValue({ ...filterValue, parameterIds: parameterIdsStr });
      }
    }
  }, [
    observationalLatestDataResult,
    obsDatasetSummariesResult,
    parameterConfigurationsResult,
    observationalPlatformsResult,
    parameterId,
    registeredParametersResult,
  ]);

  useEffect(() => {
    if (!observationalV2DataResult || !observationalV2DataResult.timestamps || observationalV2DataResult.length < 1 || parameters.length < 1) return;
    const parameterDepthsData = dataUtils.parameterToParameterDepthsData(
      parameter,
      parameters,
      observationalV2DataResult,
      selectedPeriod,
      latestDate,
      customFilterValue,
      parameterConfigurationsResult,
      unitPreferences,
      parameterId,
    );

    setParameterDepths(parameterDepthsData);

    if (comparisonParameters.length > 1) {
      const { timestamps, parameter_obs: parameterObs } = observationalV2DataResult;
      const { values } = parameterObs;
      const comparisonData = comparisonParameters.map((comparisonParameter) => {
        const comparisonValues = values[comparisonParameter.parameter_id].map((value) => formatParameterObservation(
          parameterConfigurationsResult,
          comparisonParameter,
          { value },
          unitPreferences,
        ));
        return (
          {
            name: comparisonParameter.name,
            data: comparisonValues.map((value, index) => ({
              x: timestamps[index] * 1000,
              y: `${value} ${comparisonParameter.unit}`,
              children: [],
            })),
            color: colorPalette[comparisonParameters.indexOf(comparisonParameter)],
          });
      });
      setChartData(comparisonData);
    } else if (parameterDepthsData && parameterDepthsData.length > 1) {
      setChartData(parameterDepthsData.filter((pdd) => pdd.canonicalDepthString === parameter.depth.toString()));
    } else {
      setChartData([parameterDepthsData[0]]);
    }
  }, [observationalV2DataResult, parameter, parameters, selectedPeriod, latestDate, customFilterValue, parameterConfigurationsResult, unitPreferences, parameterId]);

  const handleChangePeriod = (period) => {
    if (period === 'Custom') {
      setPickerOpen(true);
    }
    setSelectedPeriod(period);
  };

  const handleCustomFilterChange = (value) => {
    setCustomFilterValue(value);
  };

  const onDetailChange = (checked) => {
    setDataView(!checked);
  };

  const onClick = (parameter) => {
    setDataView(true);

    if (parameter.parameter_id !== Number(parameterId)) {
      history.push(`/data-console/${obsDatasetIds[0]}/parameter/${parameter.parameter_id}`);
    }
  };

  const getDepthColor = (depth) => {
    if (chartData[0] === undefined) {
      return '#000';
    }

    const dataIdx = chartData.findIndex((r) => r.name === depth.name);

    if (dataIdx > -1) {
      return colorPalette[dataIdx];
    }

    return '#000';
  };

  const onDepthClick = (depth) => {
    if (depth === 'ALL') {
      if (!isAllDepthsSelected) {
        const allDepthsData = parameterDepths.map((depth) => ({
          name: depth.name,
          data: depth.data,
        }));
        setChartData(allDepthsData);
        setIsAllDepthsSelected(true);
      } else {
        // reset to only the first depth available if "ALL" is unselected
        setChartData(parameterDepths.filter((pdd) => pdd.canonicalDepthString === parameter.depth.toString()));
        setIsAllDepthsSelected(false);
      }
    }

    formatChartData(depth);
    if (chartData.some((o) => o.name === depth.name)) {
      setChartData(chartData.filter((o) => o.name !== depth.name));
    }
  };

  const breadcrumb = [
    {
      name: obsDataset?.platform_name,
      href: `/data-console/${obsDatasetIds[0]}`,
    },
    {
      name: parameter?.name,
      href: `/data-console/${obsDatasetIds[0]}/parameter/${parameter?.parameter_id}`,
    },
  ];

  const onClickCompare = (parameter) => {
    if (comparisonParameters.includes(parameter)) {
      setComparisonParameters(comparisonParameters.filter((p) => p !== parameter));
      setParameterIds(comparisonParameters.filter((p) => p !== parameter).map((p) => p.parameter_id).join(','));
    } else {
      setComparisonParameters([...comparisonParameters, parameter]);
      setParameterIds([...comparisonParameters, parameter].map((p) => p.parameter_id).join(','));
    }
  };

  useEffect(() => {
    if (chartRef.current) {
      setChartHeight(chartRef.current.offsetHeight);
    }
  }, [chartData]);

  const onCategoriesChange = (category) => {
    setCategorySelected(category);
  };

  const chooseChartType = () => {
    if (isWindroseChart) {
      return (
        <WindroseChart
          series={chartData}
          height={isMobile ? (containerWidth - 10) : (containerHeight - 300)}
          width={containerWidth - 10}
          className="windroseStyles"
        />
      );
    }
    return (
      <CustomChart
        type={chartData[0].data.length > 1 ? 'line' : 'scatter'}
        dotsVisible={chartData[0].data.length > 1 ? dotsVisible : true}
        linesVisible={chartData[0].data.length > 1 ? linesVisible : false}
        series={chartData}
        height={containerHeight - (isMobile ? 10 : 300)}
        yAxisMin={filterEnabled ? parameter?.alerts?.threshold_min : null}
        yAxisMax={filterEnabled ? parameter?.alerts?.threshold_max : null}
      />
    );
  };

  const handleShareModalClick = async () => {
    const svgImage = await getChartImage('svg', 'seagull-share-chart', windroseSvgNode, obsDataset.platform_name, parameter.name, parameter.unit);
    const pngImage = await getChartImage('png', 'seagull-share-chart', windroseSvgNode, obsDataset.platform_name, parameter.name, parameter.unit);
    setShareImageUrl(svgImage);

    postSharingImage(svgImage, pngImage).then((objectUrl) => {
      if (objectUrl) {
        const browserUrl = new URL(window.location.href);
        const parts = objectUrl[0]?.split('/');
        const s3ObjectTokenId = parts[parts?.length - 1];
        const title = breadcrumb.map(({ name }) => name).join(' - ');
        const shareObj = { t: title, i: s3ObjectTokenId };

        browserUrl.searchParams.delete('share');
        browserUrl.searchParams.append('share', base64UrlSafeEncode(JSON.stringify(shareObj)));
        setShareImageLink(browserUrl.href);
      }
    });

    setShowShareModal(!showShareModal);
  };

  const hasUserSeenShareModal = sessionStorage.getItem('hasUserSeenShareModal');

  const chartDiv = document.getElementById('chart');

  // Checks if the chart has been touched to clear space for the tooltip on mobile
  const handleChartTooltipSpacing = () => {
    setIsChartTooltipShowing(true);
    chartDiv.removeEventListener('touchstart', handleChartTooltipSpacing);
  };

  if (chartDiv) {
    chartDiv.addEventListener('touchstart', handleChartTooltipSpacing);
  }

  const handleCommunityModalSignup = (value) => {
    setShowSignupModal(value);
  };

  const timeSeriesChart = document.getElementById('apexchartsseagull-share-chart');
  const isChartLoaded = Boolean(windroseSvgNode) || Boolean(timeSeriesChart);

  const ctaButtons = [
    {
      key: CTA_BUTTONS.CTA_FAVORITE,
      visible: false,
      isDefault: false,
    },
    {
      key: CTA_BUTTONS.CTA_CREATE_ALERT,
      visible: false,
      isDefault: false,
    },
    {
      key: CTA_BUTTONS.CTA_DOWNLOAD,
      visible: false,
      isDefault: false,
      isDisabled: !isChartLoaded,
    },
  ];

  if (featureFlag.socialSharingTimeSeries) {
    ctaButtons.unshift({
      key: CTA_BUTTONS.CTA_SHARE,
      visible: true,
      onClick: () => {
        handleShareModalClick();
        ReactGA.event(ParameterDetailsEvent.ShareModalSelect);
      },
      isDefault: false,
      isDisabled: !isChartLoaded, // Want to prevent user from clicking buttons involving chart before chart loads
    });
  }

  return (
    <div className="console-detail">
      <DetailHeader
        breadcrumb={breadcrumb}
        dataset={obsDataset}
        isMobile={isMobile}
        organization={organization}
        selectedParameter={Number(parameterId)}
        isPlatformsDataConsole={false}
        statusEl={
          obsDataset && (
            <PlatformEvent
              properties={obsDataset.obs_dataset_platform_assignment.platform.platform_event}
            />
          )
        }
        parameters={parameters}
        parameter={parameter}
        isChartLoaded={isChartLoaded}
      />
      {collectionStatus && (
        <CollectionStatusAlert collectionStatus={collectionStatus} />
      )}
      <div
        className="parameter-detail"
        style={{ padding: isMobile ? '0 10px' : '20px 0' }}
      >
        {isMobile
          ? (
            <CustomSwitch
              defaultChecked={!dataView}
              unCheckedChildren={parameter?.name}
              checkedChildren="Discover More"
              className="detail-switch"
              onChange={onDetailChange}
            />
          ) : ''}
        <Row wrap={false} style={{ flex: 1, overflow: 'hidden' }}>
          <Col
            hidden={isMobile && !dataView}
            lg={16}
            md={16}
            xs={24}
            flex={1}
            style={!isMobile ? {
              backgroundColor: brandingConfig.colors.primaryDark2,
              borderTopLeftRadius: 10,
              borderBottomLeftRadius: 10,
              overflowX: 'hidden',
              overflowY: 'scroll',
              padding: '0 20px',
            } : {
              overflowX: 'hidden',
              overflowY: 'scroll',
            }}
            className="parameter-detail-main"
            ref={containerRef}
          >
            {datasetMessage && (
              <DatasetMessage message={datasetMessage} />
            )}
            <div className={classnames(isMobile ? 'h2-w' : 'h1-w', 'd-flex', 'parameter-name')}>
              {parameter?.name}
            </div>
            {(parameterDepths.length > 1 || parameter?.description)
              && (
                <div className="parameter-description">
                  {parameter?.description}
                  {parameterDepths.length > 1 && (
                    <>
                      {' '}
                      Click on the depths on the right of the graph to see the data at those depths.
                    </>
                  )}
                </div>
              )}
            <ParameterMessage
              parameterId={parameterId}
            />
            <div
              className="text-white date-range-filter"
              style={{
                paddingBottom: 15,
                display: isMobile ? 'block' : 'flex',
                alignItems: 'center',
                fontWeight: 500,
                marginBottom: isMobile && isChartTooltipShowing ? 40 : 0,
              }}
            >
              <span>Show data from last available:</span>
              {isMobile
                ? (
                  <Select
                    className="period-select"
                    defaultValue={selectedPeriod}
                    onChange={handleChangePeriod}
                  >
                    {periods.map((period) => (
                      <Option key={period} value={period}>{period}</Option>
                    ))}
                  </Select>
                ) : (
                  <Menu
                    mode="horizontal"
                    className="period-menu"
                    onClick={(e) => handleChangePeriod(e.key)}
                    selectedKeys={selectedPeriod}
                  >
                    {periods.map((period) => (
                      <Menu.Item key={period}>
                        {period}
                      </Menu.Item>
                    ))}
                  </Menu>
                )}
              {selectedPeriod === 'Custom'
                && (
                  <RangePicker
                    className="custom-date-filter"
                    clearIcon={false}
                    format="MM-DD-YY"
                    defaultValue={customFilterValue}
                    onChange={handleCustomFilterChange}
                    separator=" - "
                    suffixIcon={<DownOutlined style={{ color: '#fff' }} />}
                    theme="dark"
                    defaultOpen={pickerOpen}
                    open={pickerOpen}
                    onOpenChange={(open) => {
                      if (open) {
                        setPickerOpen(true);
                      } else {
                        setPickerOpen(false);
                      }
                    }}
                  />
                )}
              {!isMobile && ( // Only show workspace link on desktop
                <div style={{ marginLeft: 'auto', display: 'flex' }}>
                  <CtaBar
                    buttonsConfig={ctaButtons}
                    obsDataset={obsDataset}
                  />
                  <Dropdown
                    overlay={optionsMenu}
                    placement="bottomRight"
                  >
                    <Space>
                      <Button
                        className="options-button"
                        icon={<OptionsIcon style={{ rotate: '90deg', fontSize: 27, marginRight: 5 }} />}
                        disabled={!isChartLoaded}
                      />
                    </Space>
                  </Dropdown>
                </div>
              )}
              {isMobile && (
                <Select
                  value={null}
                  placeholder="Chart options"
                  className="period-select"
                  onChange={(val) => {
                    if (val === 'toggleDots') setDotsVisible((prev) => !prev);
                    if (val === 'toggleLines') setLinesVisible((prev) => !prev);
                    if (val === 'toggleFilter') setFilterEnabled((prev) => !prev);
                  }}
                  style={{ marginTop: 8 }}
                >
                  <Option value="toggleDots">{dotsVisible ? 'Turn off data points' : 'Turn on data points'}</Option>
                  <Option value="toggleLines">{linesVisible ? 'Turn off lines' : 'Turn on lines'}</Option>
                  <Option value="toggleFilter">{filterEnabled ? 'Show full data' : 'Show filtered data'}</Option>
                </Select>
              )}
            </div>
            <Row className="parameter-chart">
              <Col xs={24}>
                {
                  (observationalV2DataIsLoading
                  || observationalLatestDataIsLoading
                  || observationalV2DataIsFetching)
                  && (chartData[0] === undefined
                    || chartData[0]?.data?.length === 0)
                    ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '18%',
                      }}
                      >
                        <Spin />
                      </div>
                    )
                    : (
                      <div
                        className={classnames('bg-dark', 'parameter-card', {
                          'depths-chart-visible': parameterDepths.length > 1,
                        })}
                        ref={chartRef}
                        style={{ border: 'none', borderRadius: 10, display: 'flex' }}
                      >
                        {observationalV2DataIsFetching && (
                          <div
                            style={{
                              position: 'absolute',
                              left: '50%',
                              top: 60,
                            }}
                          >
                            <Spin />
                          </div>
                        )}
                        {(chartData[0]?.data?.length > 0 || chartData[0] === undefined) ? (
                          <div style={{ width: chartData.length > 0 ? '100%' : '94%' }}>
                            {chartData.length > 0 && chartData[0] !== undefined
                              && chooseChartType()}
                            <div
                              className="custom-tooltip-container"
                              style={{
                                top: isMobile ? -60 : 0,
                              }}
                            >
                              <div
                                className="custom-tooltip-text"
                                style={{
                                  opacity: 0,
                                }}
                              />
                            </div>
                          </div>
                        ) : (
                          <NoDataInRange containerHeight={containerHeight} setPickerOpen={setPickerOpen} />
                        )}
                        {parameterDepths.length > 1 && (
                          <div
                            className="depths-toolbar"
                            style={{ overflowY: 'scroll', height: chartHeight }}
                          >
                            <div
                              data-color="white"
                              onClick={() => onDepthClick('ALL')}
                            >
                              ALL
                              <div style={{
                                height: 3,
                                width: '40%',
                                backgroundColor: isAllDepthsSelected ? 'white' : '',
                              }}
                              />
                            </div>
                            {parameterDepths.map((depth, index) => (
                              <div
                                key={index}
                                data-color={getDepthColor(depth, index)}
                                onClick={() => onDepthClick(depth)}
                              >
                                {depth.name}
                                <div style={{
                                  height: 3,
                                  width: '40%',
                                  backgroundColor: getDepthColor(depth, index),
                                }}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                }
              </Col>
            </Row>
          </Col>
          <Col
            hidden={isMobile && dataView}
            flex={1}
            md={8}
            xs={24}
            className="parameter-detail-others"
            style={{
              borderTopRightRadius: isMobile ? 0 : 10,
              borderBottomRightRadius: isMobile ? 0 : 10,
              overflowX: 'hidden',
              overflowY: 'scroll',
            }}
          >
            <div className="h3" style={{ color: '#fff', marginBottom: 10 }}>
              Discover More Parameters
            </div>
            <ParameterFilters onCategoriesChange={onCategoriesChange} categorySelected={categorySelected} categoriesPresent={categoryArray} />
            <FeaturedParameters
              parameters={parameters}
              onClick={(parameter) => onClick(parameter)}
              onClickCompare={(parameter) => onClickCompare(parameter)}
              comparisonParameters={comparisonParameters}
              categorySelected={categorySelected}
              categoriesPresent={categoryArray}
              handleSetCategoryArray={handleSetCategoryArray}
              isSamplingLocation={false}
              currentParameter={parameter}
            />
          </Col>
        </Row>
      </div>
      <SocialShareRecipientModal />
      <img src={`${window.location.origin}/images/seagullLogo.png`} id="logo" alt="" style={{ display: 'none' }} />
      <CustomModal
        visible={showShareModal}
        onCancel={() => {
          setShowShareModal(!showShareModal);
          if (!hasUserSeenShareModal && authStatus !== 'authenticated') {
            sessionStorage.setItem('hasUserSeenShareModal', true);
            setShowSignupModal(true);
          }
        }}
        footer={null}
        width="650px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        style={{ top: isMobile ? null : '50%', translate: isMobile ? null : '0 -50%' }}
        className="socialShare"
        isSocialShare
      >
        <SocialShareSenderModal
          shareImageLink={shareImageLink}
          shareImageUrl={shareImageUrl}
          onDownloadPng={onDownloadPng}
        />
      </CustomModal>
      <CustomModal
        visible={showSignupModal}
        onCancel={() => setShowSignupModal(false)}
        footer={null}
        width="605px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        style={{ top: isMobile ? null : '50%', translate: isMobile ? null : '0 -50%' }}
        className="socialShare communitySignup"
        isSocialShare
        isCommunityModal
      >
        <CommunitySignupModal handleCommunityModalSignup={handleCommunityModalSignup} />
      </CustomModal>
      <CustomModal
        visible={showDownloadChartModal}
        onCancel={() => setShowDownloadChartModal(!showDownloadChartModal)}
        footer={null}
        width="690px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        style={isMobile ? { height: '100%', display: 'flex', top: 0 } : ''}
        className="downloadChartModal"
      >
        <DownloadChartModal onDownloadPng={onDownloadPng} onDownloadSvg={onDownloadSvg} />
      </CustomModal>
      <div hidden><canvas id="canvas-holder" ref={canvasRef} /></div>
      {/* Hidden chart for social sharing and high-res download */}
      {chartData[0]?.data?.length > 0 && (
        <>
          <div style={{
            position: 'absolute',
            zIndex: -1,
            visibility: 'hidden',
            top: -9999,
            left: -9999,
          }}
          >
            {!isWindroseChart
            && (
              <CustomChart
                type={chartData[0]?.data?.length > 1 ? 'line' : 'scatter'}
                dotsVisible={chartData[0]?.data?.length > 1 ? dotsVisible : true}
                linesVisible={chartData[0]?.data?.length > 1 ? linesVisible : false}
                series={chartData}
                height={isMobile ? 200 : 250}
                width={isMobile ? 500 : 650}
                chartId="seagull-share-chart"
                isSocialChart
                yAxisMin={filterEnabled ? parameter?.alerts?.threshold_min : null}
                yAxisMax={filterEnabled ? parameter?.alerts?.threshold_max : null}
              />
            )}
            {isWindroseChart
            && (
              <WindroseChart
                series={chartData}
                height={isMobile ? (containerWidth - 10) : (containerHeight - 300)}
                width={containerWidth - 10}
                className="windroseStyles"
                id="seagull-windrose-chart"
              />
            )}
          </div>
          <div style={{
            position: 'absolute',
            zIndex: -1,
            visibility: 'hidden',
            top: -9999,
            left: -9999,
          }}
          >
            {!isWindroseChart
            && (
              <CustomChart
                type={chartData[0].data.length > 1 ? 'line' : 'scatter'}
                dotsVisible={chartData[0].data.length > 1 ? dotsVisible : true}
                linesVisible={chartData[0].data.length > 1 ? linesVisible : false}
                series={chartData}
                height={800 - 100}
                width={800 * (4 / 3)}
                isHighResChart
                chartId="seagull-share-chart-high-res"
                yAxisMin={filterEnabled ? parameter?.alerts?.threshold_min : null}
                yAxisMax={filterEnabled ? parameter?.alerts?.threshold_max : null}
              />
            )}
            {isWindroseChart
            && (
              <WindroseChart
                series={chartData}
                height={800 - 100}
                width={800}
                className="windroseStyles"
                id="seagull-windrose-chart-high-res"
                isHighResChart
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

ParameterDetail.propTypes = {};

ParameterDetail.defaultProps = {};

export default memo(ParameterDetail);
